<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import { useAppToast } from '@/shared/lib/useAppToast'
import { getImportJob, getImportOrchestration } from '../api/imports.service'
import { enumLabel, formatDate, importStatuses, isImportParseReady, stateSeverity } from '../lib/importDisplay'
import ImportSourceParsePanel from '../components/ImportSourceParsePanel.vue'
import ImportColumnMappingPanel from '../components/ImportColumnMappingPanel.vue'

const route = useRoute()
const router = useRouter()
const toast = useAppToast()
const jobId = computed(() => String(route.params.jobId))
const details = ref(null)
const loading = ref(false)
const activeSection = ref('parse')
const sourceRef = ref(null)
const columnMappingRef = ref(null)
const parseReady = computed(() => isImportParseReady(details.value?.job))
function switchSection(section) {
  if (section === activeSection.value) return
  if (section !== 'parse' && !parseReady.value) {
    toast.warn('Сначала дождитесь успешного завершения разбора файла.')
    activeSection.value = 'parse'
    return
  }
  if (activeSection.value === 'columns' && columnMappingRef.value && !columnMappingRef.value.confirmLeave()) return
  activeSection.value = section
}
const jobPollTimer = ref(null)
const sections = [
  { key: 'parse', label: 'Sources / Parse', icon: 'pi pi-table' },
  { key: 'columns', label: 'Excel Columns', icon: 'pi pi-list' }
]

async function loadDetails(useCanonicalRoute = false) {
  loading.value = true
  try {
    details.value = useCanonicalRoute
      ? await getImportOrchestration(jobId.value)
      : await getImportJob(jobId.value)
  } catch (error) {
    toast.error(error.message)
    if (error.status === 404) router.push('/admin/imports')
  } finally { loading.value = false }
}

function stopJobPolling() {
  if (jobPollTimer.value) clearTimeout(jobPollTimer.value)
  jobPollTimer.value = null
}

async function pollParsing() {
  stopJobPolling()
  await loadDetails()
  const status = details.value?.job?.status
  if ([2, 3, 'QueuedForParsing', 'Parsing'].includes(status)) {
    jobPollTimer.value = setTimeout(pollParsing, 1200)
  } else {
    await nextTick()
    sourceRef.value?.loadStructure()
  }
}

onMounted(async () => {
  await loadDetails(true)
  if ([2, 3, 'QueuedForParsing', 'Parsing'].includes(details.value?.job?.status)) pollParsing()
})
onBeforeUnmount(stopJobPolling)
</script>

<template>
  <main class="workspace-page">
    <div class="page-shell">
      <header class="workspace-header">
        <div>
          <Button label="Все импорты" icon="pi pi-arrow-left" text @click="router.push('/admin/imports')" />
          <div class="title-row">
            <h1>{{ details?.job?.sourceSystem || 'Import workspace' }}</h1>
            <Tag v-if="details" :value="enumLabel(importStatuses, details.job.status)" :severity="stateSeverity(details.job.status)" />
          </div>
          <p v-if="details">Создан {{ formatDate(details.job.createdAt) }} · {{ details.job.createdByName }} · <code>{{ details.job.id }}</code></p>
        </div>
        <Button icon="pi pi-refresh" label="Обновить" outlined :loading="loading" @click="loadDetails" />
      </header>

      <nav class="section-nav" aria-label="Разделы импорта">
        <button v-for="section in sections" :key="section.key" :class="{ active: activeSection === section.key, disabled: section.key !== 'parse' && !parseReady }" :disabled="section.key !== 'parse' && !parseReady" @click="switchSection(section.key)">
          <i :class="section.icon"></i><span>{{ section.label }}</span>
          <span v-if="section.key === 'parse' && details?.job?.activeParseAttemptId" class="dot success"></span>
        </button>
      </nav>

      <div v-if="loading && !details" class="loading-state"><i class="pi pi-spin pi-spinner"></i> Загрузка workspace…</div>
      <template v-else-if="details">
        <ImportSourceParsePanel v-show="activeSection === 'parse'" ref="sourceRef" :job-id="jobId" :details="details"
          @changed="loadDetails" @poll="pollParsing" />
        <ImportColumnMappingPanel v-if="activeSection === 'columns'" ref="columnMappingRef" :job-id="jobId" :details="details" />
      </template>
    </div>
  </main>
</template>

<style scoped>
.workspace-page { min-height: 100vh; background: #f7f7f6; }.page-shell { max-width: 1380px; margin: 0 auto; padding: 24px 24px 52px; }
.workspace-header { display: flex; justify-content: space-between; align-items: flex-end; gap: 20px; }.workspace-header h1 { margin: 8px 0 0; font-size: clamp(26px, 4vw, 38px); letter-spacing: -.04em; }.workspace-header p { margin: 7px 0 0; color: #777; font-size: 12px; }.workspace-header code { font-size: 10px; }.title-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.section-nav { display: flex; gap: 6px; padding: 6px; margin: 22px 0 16px; border: 1px solid #e2e2df; background: #fff; border-radius: 15px; overflow-x: auto; }.section-nav button { position: relative; border: 0; background: transparent; padding: 11px 14px; border-radius: 10px; display: flex; align-items: center; gap: 8px; color: #666; white-space: nowrap; cursor: pointer; }.section-nav button.active { background: #171717; color: white; }.dot { width: 7px; height: 7px; border-radius: 50%; }.dot.success { background: #48b36d; }
.loading-state { padding: 70px; text-align: center; color: #777; }
@media (max-width: 700px) { .page-shell { padding: 16px 10px 40px; }.workspace-header { align-items: stretch; flex-direction: column; }.section-nav button { padding: 10px; }.section-nav button span:not(.dot) { display: none; } }
</style>
